import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import {
  AlertCircle,
  AlertTriangle,
  BookOpen,
  BookOpenCheck,
  Box,
  ClipboardCheck,
  ClipboardX,
  Search,
  ShoppingCart,
  Users,
} from "lucide-react"
import { getAccessibleRoutePaths } from "shared/procurement/accessibleRoutes"
import { Role, getRoleCode } from "constant/Role"
import {
  useGetItemsQuery,
  useGetProcurementCentersQuery,
  useGetBookInventoryOverviewQuery,
} from "shared/redux/endpoints/procurementItems"
import {
  EmptyState,
  ErrorState,
  ExportButton,
  FilterBar,
  KpiStrip,
  KpiTile,
  SegmentedControl,
  en,
} from "procurement/design"
import { Page, Button } from "./ui"
import useProcurementFilters from "./hooks/useProcurementFilters"
import useBookDemand from "./bookInventory/useBookDemand"
import DemandDetailDrawer from "./components/bookInventory/DemandDetailDrawer"
import DemandGroupTable from "./components/bookInventory/DemandGroupTable"
import CreateOrderModal from "./components/bookInventory/CreateOrderModal"
import ViewStockModal from "./components/bookInventory/ViewStockModal"
import TransferStockModal from "./components/bookInventory/TransferStockModal"
import {
  WINDOW_OPTIONS,
  at,
  buildOrderPrefill,
  demandRowKey,
  demandStatus,
  filterDemandRows,
  formatEstimated,
  overviewAt,
  formatQty,
  groupDemandRows,
  rowStock,
  windowDays,
} from "./bookInventory/model"

const FILTER_DEFAULTS = {
  centerId: "",
  itemId: "",
  window: "30",
  shortage: "",
  orStatus: "",
  search: "",
  kpi: "",
  groupBy: "center",
}

// The legacy still-needed / gap columns stay in the CSV only.
const exportColumns = (days) => [
  { label: "Center", value: (row) => row.centerName || row.centerId },
  { label: "Item code", value: (row) => row.itemCode },
  { label: "Item", value: (row) => row.itemName },
  { label: `Actual demand +30% (${days} days)`, value: (row) => at(row, "actualNeeded", days) },
  { label: `Estimated demand +30% (${days} days)`, value: (row) => at(row, "estimatedNeeded", days) },
  { label: "Stock", value: rowStock },
  { label: `Actual shortage (${days} days)`, value: (row) => at(row, "actualShortage", days) },
  { label: `Estimated shortage (${days} days)`, value: (row) => at(row, "estimatedShortage", days) },
  { label: "OR status", value: (row) => row.orderStatus },
  { label: "OR requested qty", value: (row) => row.orRequestedQty },
  { label: "Status", value: (row) => demandStatus(row, days).label },
  { label: "Still needed next week", value: (row) => row.neededNextWeek },
  { label: "Still needed next month", value: (row) => row.neededNextMonth },
  { label: "Gap (7 days)", value: (row) => row.gapNextWeek },
  { label: "Gap (30 days)", value: (row) => row.gapNextMonth },
]

const SHORTAGE_WINDOWS = [7, 30, 90]

// Each block sets the filters it stands for; clicking an active one clears
// them. "With OR" is a 90-day count (OR status has no time window), so it
// switches the table to 90 days; the other OR / shortage blocks follow the
// selected window.
const KPI_FILTERS = {
  need7: { window: "7" },
  need30: { window: "30" },
  short: { shortage: "actual" },
  withOr: { orStatus: "HAS_OR", window: "90" },
  withoutOr: { orStatus: "NO_OR", shortage: "actual" },
}
const isWindowKpi = (key) => /^(need|shortage)/.test(key || "")

// Same roles the BE allows for Reserve / Issue, matched on the active role.
const CAN_TRANSFER_ROLES = [Role.PROCUREMENT, Role.PROCUREMENT_MANAGER, Role.ADMIN, Role.SUPPER_ADMIN]

const BookInventoryContainer = () => {
  const navigate = useNavigate()
  const auth = useSelector((state) => state.auth)
  const canCreateOrder = useMemo(
    () => getAccessibleRoutePaths(auth).has("/procurement/order-requests/new"),
    [auth]
  )
  const canTransfer = CAN_TRANSFER_ROLES.includes(getRoleCode(auth?.role))
  const [transferRow, setTransferRow] = useState(null)
  const { filters, setFilters, resetFilters } = useProcurementFilters(FILTER_DEFAULTS)
  const [selected, setSelected] = useState(() => new Set())
  const [detail, setDetail] = useState(null)
  const [orderRow, setOrderRow] = useState(null)
  const [stockRow, setStockRow] = useState(null)

  const { data: items } = useGetItemsQuery({ size: 10000 })
  const { data: centersPage } = useGetProcurementCentersQuery({ size: 200 })
  const { data: overview, isFetching: overviewLoading } = useGetBookInventoryOverviewQuery({
    centerId: filters.centerId || undefined,
  })
  const demand = useBookDemand({ centerId: filters.centerId, itemId: filters.itemId })

  const itemsById = useMemo(() => {
    const map = new Map()
    ;(items || []).forEach((item) => {
      if (item?.id != null) map.set(String(item.id), item)
    })
    return map
  }, [items])

  const days = windowDays(filters.window)
  const filtered = useMemo(
    () =>
      filterDemandRows(demand.rows, {
        search: filters.search,
        window: filters.window || "30",
        shortage: filters.shortage,
        orStatus: filters.orStatus,
      }),
    [demand.rows, filters.search, filters.window, filters.shortage, filters.orStatus]
  )
  const groupBy = filters.groupBy === "item" ? "item" : "center"
  const groups = useMemo(() => groupDemandRows(filtered, groupBy, days), [filtered, groupBy, days])
  const centerCount = useMemo(() => new Set(filtered.map((row) => String(row.centerId))).size, [filtered])

  useEffect(() => {
    if (demand.isLoading) return
    const visible = new Set(filtered.map(demandRowKey))
    setSelected((prev) => {
      const next = new Set([...prev].filter((key) => visible.has(key)))
      return next.size === prev.size ? prev : next
    })
  }, [filtered, demand.isLoading])

  const centerOptions = useMemo(() => {
    const map = new Map()
    ;(centersPage?.items || []).forEach((center) => {
      if (center?.centerId == null) return
      map.set(String(center.centerId), center.centerName || `Center ${center.centerId}`)
    })
    demand.rows.forEach((row) => {
      if (row.centerId == null || map.has(String(row.centerId))) return
      map.set(String(row.centerId), row.centerName || `Center ${row.centerId}`)
    })
    return Array.from(map, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label))
  }, [centersPage, demand.rows])

  const bookOptions = useMemo(() => {
    const map = new Map()
    ;(items || []).forEach((item) => {
      if (item?.id == null) return
      map.set(String(item.id), item.name || item.itemCode || `Item ${item.id}`)
    })
    demand.rows.forEach((row) => {
      if (row.itemId == null || map.has(String(row.itemId))) return
      map.set(String(row.itemId), row.itemName || row.itemCode || `Item ${row.itemId}`)
    })
    return Array.from(map, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label))
  }, [items, demand.rows])

  const selectedRows = filtered.filter((row) => selected.has(demandRowKey(row)))

  const toggleOne = (row) => {
    const key = demandRowKey(row)
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const openOrder = (rows, overrides) => {
    if (!rows?.length || !canCreateOrder) return
    navigate("/procurement/order-requests/new", {
      state: { bookInventory: buildOrderPrefill(rows, itemsById, overrides, days) },
    })
  }

  const rowMenu = (row) =>
    [
      { label: "View demand details", onClick: () => setDetail(row) },
      row.itemId != null && { label: "View stock", onClick: () => setStockRow(row) },
      canTransfer &&
        row.itemId != null && { label: "Transfer from another center", onClick: () => setTransferRow(row) },
      canCreateOrder &&
        row.itemId != null && {
          label: "Create order request",
          highlight: demandStatus(row, days).key === "short",
          onClick: () => setOrderRow(row),
        },
    ].filter(Boolean)

  const activeKpi = filters.kpi || ""
  const applyKpi = (key, patch = KPI_FILTERS[key]) => {
    if (activeKpi === key) setFilters({ kpi: "", shortage: "", orStatus: "" })
    else setFilters({ shortage: "", orStatus: "", ...patch, kpi: key })
  }

  const ready = overview && !overviewLoading
  const kpi = (field) => (ready ? formatQty(overview[field]) : "—")

  const shortageHint = (d) => {
    if (!ready) return null
    const unknownBooks = Number(overview[`actualUnknownItems${d}d`]) || 0
    const missingSource = Number(overview[`estimatedMissingSourceClassBooks${d}d`]) || 0
    const notes = [
      unknownBooks > 0 && `${formatQty(unknownBooks)} books lack data (not mapped to A/B sessions)`,
      missingSource > 0 && `${formatQty(missingSource)} class × book rows missing the source class size`,
    ].filter(Boolean)
    return notes.length ? <span className="bi-kpi-warn">{notes.join(" · ")}</span> : scopeHint
  }
  const scopeHint = filters.centerId ? "At this center" : "Across all centers"
  const serverFiltered = Boolean(filters.centerId || filters.itemId)
  const clientFiltered = Boolean(
    filters.search || filters.shortage || filters.orStatus || (filters.window && filters.window !== "30")
  )
  const denied = demand.error?.status === 403

  return (
    <Page breadcrumb={["Procurement", "Book Inventory"]} title={en.bookInventory.title} subtitle={en.bookInventory.subtitle}>
      <KpiStrip>
        <KpiTile
          icon={<Users aria-hidden />}
          iconTone="info"
          label={`Upcoming classes (${days} days)`}
          value={kpi(`upcomingClasses${days}d`)}
          hint="Classes need book distribution soon"
        />
        <KpiTile
          icon={<BookOpen aria-hidden />}
          iconTone="info"
          label="Books still needed (7 days)"
          value={kpi("totalBooksNeeded7d")}
          hint={scopeHint}
          active={activeKpi === "need7"}
          onClick={() => applyKpi("need7")}
        />
        <KpiTile
          icon={<BookOpenCheck aria-hidden />}
          iconTone="warning"
          label="Books still needed (30 days)"
          value={kpi("totalBooksNeeded30d")}
          hint={scopeHint}
          active={activeKpi === "need30"}
          onClick={() => applyKpi("need30")}
        />
        <KpiTile
          icon={<AlertCircle aria-hidden />}
          iconTone="danger"
          label="Items with shortage"
          value={kpi("itemsWithShortage")}
          hint="Need to purchase / transfer"
          active={activeKpi === "short"}
          onClick={() => applyKpi("short")}
        />
        <KpiTile
          icon={<Box aria-hidden />}
          iconTone="success"
          label="Current stock (books)"
          value={kpi("currentStockBooks")}
          hint={scopeHint}
        />
        <KpiTile
          icon={<ClipboardCheck aria-hidden />}
          label="With OR"
          value={kpi("centerBooksWithOr90d")}
          hint={ready ? `Center × Book, 90 days · ${formatQty(overview.orRequestedQty)} books on OR` : "Center × Book, 90 days"}
          active={activeKpi === "withOr"}
          onClick={() => applyKpi("withOr")}
        />
        <KpiTile
          icon={<ClipboardX aria-hidden />}
          iconTone="warning"
          label="Shortage without OR"
          value={kpi(`shortageCenterBooksWithoutOr${days}d`)}
          hint={`Center × Book without an OR, short in ${days} days`}
          active={activeKpi === "withoutOr"}
          onClick={() => applyKpi("withoutOr")}
        />
        {SHORTAGE_WINDOWS.map((d) => (
          <KpiTile
            key={d}
            className="bi-kpi-wide"
            icon={<AlertTriangle aria-hidden />}
            iconTone="danger"
            label={`Shortage – ${d} days`}
            value={
              <div className="bi-kpi-pair">
                {[
                  [
                    "actual",
                    "Actual +30%",
                    ready
                      ? formatEstimated(overviewAt(overview, "actualShortage", d), overview[`actualShortageKnown${d}d`])
                      : "—",
                  ],
                  [
                    "estimated",
                    "Estimated +30%",
                    ready
                      ? formatEstimated(
                          overviewAt(overview, "estimatedShortage", d),
                          overview[`estimatedShortageKnown${d}d`]
                        )
                      : "—",
                  ],
                ].map(([type, label, value]) => (
                  <button
                    key={type}
                    type="button"
                    className={`bi-kpi-half${activeKpi === `shortage${d}-${type}` ? " is-active" : ""}`}
                    aria-pressed={activeKpi === `shortage${d}-${type}`}
                    onClick={() => applyKpi(`shortage${d}-${type}`, { window: String(d), shortage: type })}
                  >
                    <span>{label}</span>
                    {value}
                  </button>
                ))}
              </div>
            }
            hint={shortageHint(d)}
            active={activeKpi.startsWith(`shortage${d}-`)}
          />
        ))}
      </KpiStrip>

      <FilterBar
        title={en.bookInventory.filters}
        values={filters}
        onChange={(patch) =>
          setFilters("window" in patch && isWindowKpi(activeKpi) ? { ...patch, kpi: "", shortage: "" } : patch)
        }
        onReset={() => resetFilters()}
        fields={[
          {
            key: "centerId",
            label: en.filter.center,
            type: "select",
            placeholder: en.filter.allCenters,
            options: centerOptions,
          },
          {
            key: "itemId",
            label: "Item",
            type: "select",
            placeholder: "All items",
            options: bookOptions,
          },
          {
            key: "window",
            label: "Time range",
            type: "select",
            allowEmpty: false,
            defaultValue: "30",
            options: WINDOW_OPTIONS,
          },
        ]}
      />

      {selectedRows.length > 0 && (
        <div className="bi-selectbar" role="status">
          <span>
            <strong>{en.common.selected(selectedRows.length)}</strong>
            <span style={{ color: "var(--text-secondary)" }}> · {en.bookInventory.selectionHint}</span>
          </span>
          <span style={{ display: "inline-flex", gap: 8 }}>
            <Button size="sm" onClick={() => setSelected(new Set())}>
              {en.bookInventory.clear}
            </Button>
            {canCreateOrder && (
              <Button size="sm" variant="primary" leftIcon={<ShoppingCart size={14} aria-hidden />} onClick={() => openOrder(selectedRows)}>
                {en.bookInventory.createOrder}
              </Button>
            )}
          </span>
        </div>
      )}

      {demand.isError ? (
        <ErrorState
          title={denied ? en.bookInventory.deniedTitle : en.empty.loadErrorTitle}
          text={denied ? en.bookInventory.deniedText : en.empty.loadErrorText}
          action={
            <Button size="sm" onClick={() => demand.refetch()}>
              {en.common.retry}
            </Button>
          }
        />
      ) : (
        <div className="ds-table-shell">
          <div className="ds-table-toolbar">
            <strong className="bi-table-title">
              {demand.isLoading ? "Loading…" : `${centerCount} centers · ${filtered.length} items · Next ${days} days`}
            </strong>
            <div className="ds-table-toolbar-right">
              <div className="pc-input-wrap bi-search">
                <Search className="pc-lead" aria-hidden />
                <input
                  type="search"
                  className="pc-control pc-has-lead"
                  placeholder="Search book name..."
                  aria-label="Search book name"
                  value={filters.search}
                  onChange={(event) => setFilters({ search: event.target.value })}
                />
              </div>
              <SegmentedControl
                value={groupBy}
                onChange={(value) => setFilters({ groupBy: value === "center" ? "" : value })}
                options={[
                  { value: "center", label: "Group by Center" },
                  { value: "item", label: "Group by Item" },
                ]}
              />
              <ExportButton rows={filtered} columns={exportColumns(days)} filename="book-inventory.csv" />
            </div>
          </div>
          {demand.isLoading ? (
            <p className="bi-muted" style={{ padding: 16 }}>Loading book demand…</p>
          ) : groups.length === 0 ? (
            <EmptyState
              icon={<BookOpen aria-hidden />}
              title={
                !serverFiltered && !clientFiltered && !demand.rows.length
                  ? en.bookInventory.emptyTitle
                  : en.empty.noResultsTitle
              }
              text={
                !serverFiltered && !clientFiltered && !demand.rows.length
                  ? en.bookInventory.emptyText
                  : en.empty.noResultsText
              }
            />
          ) : (
            <DemandGroupTable
              key={groupBy}
              groups={groups}
              groupBy={groupBy}
              days={days}
              selected={selected}
              onToggleRow={canCreateOrder ? toggleOne : null}
              onOpenRow={setDetail}
              rowMenu={rowMenu}
            />
          )}
        </div>
      )}

      <DemandDetailDrawer
        row={detail}
        open={!!detail}
        daysWindow={days}
        onClose={() => setDetail(null)}
        canCreateOrder={canCreateOrder}
        onViewStock={setStockRow}
        onTransfer={canTransfer ? setTransferRow : null}
        onCreateOrder={(row) => {
          setDetail(null)
          setOrderRow(row)
        }}
      />
      <ViewStockModal open={!!stockRow} row={stockRow} onClose={() => setStockRow(null)} />
      <TransferStockModal open={!!transferRow} row={transferRow} daysWindow={days} onClose={() => setTransferRow(null)} />
      <CreateOrderModal
        open={!!orderRow}
        row={orderRow}
        daysWindow={days}
        onClose={() => setOrderRow(null)}
        onContinue={(row, overrides) => {
          setOrderRow(null)
          openOrder([row], overrides)
        }}
      />
    </Page>
  )
}

export default BookInventoryContainer
